<script setup lang="ts">
import { ref, computed, type Ref } from "vue";
import { useCanvasScrollbars } from "~/composables/useCanvasScrollbars";

const props = defineProps<{
  stageSize: { width: number; height: number };
  contentSize: { width: number; height: number };
}>();

const stageSizeRef = computed(() => props.stageSize) as Ref<{
  width: number;
  height: number;
}>;
const contentSizeRef = computed(() => props.contentSize) as Ref<{
  width: number;
  height: number;
}>;

const {
  isVisible,
  visibleX,
  visibleY,
  thumbRatioX,
  thumbRatioY,
  thumbPosX,
  thumbPosY,
  setPositionFromThumbX,
  setPositionFromThumbY,
} = useCanvasScrollbars(stageSizeRef, contentSizeRef);

const TRACK_SIZE = 14;

const hThumbStyle = computed(() => {
  const trackWidth = props.stageSize.width - (visibleY.value ? TRACK_SIZE : 0);
  const width = thumbRatioX.value * trackWidth;
  const left = thumbPosX.value * (trackWidth - width);
  return { left: `${left}px`, width: `${width}px` };
});

const vThumbStyle = computed(() => {
  const trackHeight =
    props.stageSize.height - (visibleX.value ? TRACK_SIZE : 0);
  const height = thumbRatioY.value * trackHeight;
  const top = thumbPosY.value * (trackHeight - height);
  return { top: `${top}px`, height: `${height}px` };
});

const isDraggingH = ref(false);
const isDraggingV = ref(false);

function onHThumbDown(e: PointerEvent) {
  isDraggingH.value = true;
  (e.target as HTMLElement).setPointerCapture(e.pointerId);
}

function onHThumbMove(e: PointerEvent) {
  if (!isDraggingH.value) return;
  const track = (e.target as HTMLElement).parentElement!;
  const rect = track.getBoundingClientRect();
  const trackWidth = rect.width;
  const thumbWidth = thumbRatioX.value * trackWidth;
  const pos =
    (e.clientX - rect.left - thumbWidth / 2) / (trackWidth - thumbWidth);
  setPositionFromThumbX(pos);
}

function onHThumbUp() {
  isDraggingH.value = false;
}

function onVThumbDown(e: PointerEvent) {
  isDraggingV.value = true;
  (e.target as HTMLElement).setPointerCapture(e.pointerId);
}

function onVThumbMove(e: PointerEvent) {
  if (!isDraggingV.value) return;
  const track = (e.target as HTMLElement).parentElement!;
  const rect = track.getBoundingClientRect();
  const trackHeight = rect.height;
  const thumbHeight = thumbRatioY.value * trackHeight;
  const pos =
    (e.clientY - rect.top - thumbHeight / 2) / (trackHeight - thumbHeight);
  setPositionFromThumbY(pos);
}

function onVThumbUp() {
  isDraggingV.value = false;
}

function onHTrackClick(e: MouseEvent) {
  if ((e.target as HTMLElement).classList.contains("scrollbar-thumb")) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const trackWidth = rect.width;
  const thumbWidth = thumbRatioX.value * trackWidth;
  const pos =
    (e.clientX - rect.left - thumbWidth / 2) / (trackWidth - thumbWidth);
  setPositionFromThumbX(pos);
}

function onVTrackClick(e: MouseEvent) {
  if ((e.target as HTMLElement).classList.contains("scrollbar-thumb")) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const trackHeight = rect.height;
  const thumbHeight = thumbRatioY.value * trackHeight;
  const pos =
    (e.clientY - rect.top - thumbHeight / 2) / (trackHeight - thumbHeight);
  setPositionFromThumbY(pos);
}
</script>

<template>
  <div v-show="isVisible" class="canvas-scrollbars">
    <div
      v-show="visibleX"
      class="scrollbar-track horizontal"
      :style="{ right: visibleY ? `${TRACK_SIZE}px` : '0' }"
      @click="onHTrackClick"
    >
      <div
        class="scrollbar-thumb"
        :style="hThumbStyle"
        @pointerdown.stop="onHThumbDown"
        @pointermove="onHThumbMove"
        @pointerup="onHThumbUp"
        @lostpointercapture="onHThumbUp"
      />
    </div>

    <div
      v-show="visibleY"
      class="scrollbar-track vertical"
      :style="{ bottom: visibleX ? `${TRACK_SIZE}px` : '0' }"
      @click="onVTrackClick"
    >
      <div
        class="scrollbar-thumb"
        :style="vThumbStyle"
        @pointerdown.stop="onVThumbDown"
        @pointermove="onVThumbMove"
        @pointerup="onVThumbUp"
        @lostpointercapture="onVThumbUp"
      />
    </div>

    <div v-show="visibleX && visibleY" class="scrollbar-corner" />
  </div>
</template>

<style scoped>
@media (pointer: fine) {
  .canvas-scrollbars {
    position: absolute;
    inset: 0;
    z-index: 95;
    pointer-events: none;
  }

  .scrollbar-track {
    position: absolute;
    background: #f0f0f0;
    pointer-events: auto;
  }

  .scrollbar-track.horizontal {
    left: 0;
    bottom: 0;
    height: 14px;
    border-top: 1px solid #ddd;
  }

  .scrollbar-track.vertical {
    right: 0;
    top: 0;
    width: 14px;
    border-left: 1px solid #ddd;
  }

  .scrollbar-thumb {
    position: absolute;
    background: #888;
    border-radius: 6px;
    cursor: grab;
  }

  .scrollbar-thumb:hover,
  .scrollbar-thumb:active {
    background: #666;
  }

  .scrollbar-thumb:active {
    cursor: grabbing;
  }

  .scrollbar-track.horizontal .scrollbar-thumb {
    top: 2px;
    bottom: 2px;
    min-width: 20px;
  }

  .scrollbar-track.vertical .scrollbar-thumb {
    left: 2px;
    right: 2px;
    min-height: 20px;
  }

  .scrollbar-corner {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 14px;
    height: 14px;
    background: #e0e0e0;
    pointer-events: auto;
  }
}

@media (pointer: coarse) {
  .canvas-scrollbars {
    display: none;
  }
}
</style>
